/**
 * THE DATA-QUALITY PANEL — one calm place on Insights that gathers every gap the
 * board's own numbers lean on, names the metric each one distorts, and offers to
 * fix it in a tap (project-idle-nudge).
 *
 * It reads as housekeeping, not an alarm: a count, the plain cost to the numbers
 * people trust, and one action per row. The ROLL-UP is pure (src/data-quality.ts);
 * this file only turns it into words and wires the actions. The wording lives here
 * on purpose, so the copy can change without touching the rules.
 *
 * Scoped to THIS board — data quality is a board you tidy, not a portfolio you
 * read — so it reads the board's own cards, phases and sprints rather than the
 * Insights scope toggle.
 */
import { useMemo } from 'react';
import { useApp } from '../store';
import { dataQuality, STALE_TODO_DAYS, type DQKind, type DQCleanDim } from '../data-quality';
import { DEFAULT_IDLE_WINDOWS } from '../idle-nudge';
import { suggestSize, suggestDue } from '../suggest';
import { ownerOf } from '../forecast';
import type { Phase } from '../phases';
import type { Sprint } from '../sprints';
import type { Issue } from '../types';
import { confirmDialog } from '../confirm';
import { can } from '../permissions';
import Icon, { type IconName } from './Icon';
import InfoTip from './InfoTip';

/** The glyph each gap wears — all neutral, none red: this is tidying, not a fault. */
const GAP_ICON: Record<DQKind, IconName> = {
  idle: 'clock',
  'stale-todo': 'package',
  'no-size': 'list-bullet',
  'no-due': 'calendar',
};

/** The green "all set" line each clean dimension reads as. */
const CLEAN_LABEL: Record<DQCleanDim, string> = {
  idle: 'Every started card has moved recently.',
  'stale-todo': 'No To-Do card is sitting as backlog.',
  'no-size': 'Every card is sized.',
  'no-due': 'Every active card has a due date.',
  'epic-area': 'Epics and areas are set across the board.',
};

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** How the drill-to-cards banner finishes the sentence "Showing the N cards …".
 *  The three field gaps read the same at any count; only idle carries a verb, so
 *  it is resolved per-count by `showWhy` below (L158 — "1 card that has gone
 *  quiet", "3 cards that have gone quiet"). */
const SHOW_WHY: Record<DQKind, string> = {
  idle: 'that have gone quiet',
  'stale-todo': 'sitting as backlog',
  'no-size': 'with no size',
  'no-due': 'with no due date',
};
const showWhy = (kind: DQKind, n: number): string =>
  kind === 'idle' ? `that ${plural(n, 'has', 'have')} gone quiet` : SHOW_WHY[kind];

export default function DataQualityPanel() {
  const issues = useApp((s) => s.issues);
  const phases = useApp((s) => s.phases);
  const sprints = useApp((s) => s.sprints);
  const boardName = useApp((s) => s.meta.name?.trim() || 'This board');
  // This board's own idle windows (0213) — null/absent falls back to the app
  // defaults, so the panel's "gone quiet" count honours the board's tuning.
  const idleWindows = useApp((s) => s.meta.idleWindows ?? DEFAULT_IDLE_WINDOWS);
  // The workspace working calendar (0216) — so "gone quiet" counts only working
  // days: a holiday the whole workspace closed for is not a doer letting work sit.
  const workCalendar = useApp((s) => s.workCalendar);
  const updateIssue = useApp((s) => s.updateIssue);
  const moveIssues = useApp((s) => s.moveIssues);
  const showFlash = useApp((s) => s.showFlash);
  // SHOW ME WHICH CARDS — every row opens its exact cards, the same "drill to this
  // set" route the other Insight panels use (showInBacklog). A diagnostic that names
  // a count has to let you SEE and reach those cards, or it is a dead end (Vinnie,
  // 25 Oct; discoverability is correctness, data must be valuable).
  const showInBacklog = useApp((s) => s.showInBacklog);

  /*
   * EVERYONE SEES, MANAGERS ACT (Vinnie, 24 Oct 2026). The panel is open to
   * everyone who reaches Insights — data quality is something the whole team
   * should be able to SEE, so no one is surprised by a number the board bends.
   * But the BULK actions — fill every missing size, move a batch to Backlog —
   * are a board-running move, so they sit behind `editSettings` (rank 4), the
   * same capability the Plan, the budget ledger and the Portfolio gate on. A
   * member still fixes their OWN cards in place through the inline card assist;
   * what they do not get here is the one-tap bulk write across other people's
   * work. (Vendors never reach this screen — their sidebar omits Insights — so
   * the external-viewer concern is handled upstream, not here.)
   */
  const role = useApp((s) => s.shownRole);
  const canAct = can(role, 'editSettings');

  const dq = useMemo(
    () => dataQuality(issues, phases, sprints, idleWindows, Date.now(), workCalendar),
    [issues, phases, sprints, idleWindows, workCalendar],
  );

  // Does the panel actually offer a one-tap action right now? A manager only gets
  // bulk controls on rows that HAVE one — a stale-To-Do row (Move to Backlog) or a
  // no-size/no-due row the suggester can fill (suggestable > 0). When every gap is
  // non-suggestable, there is nothing to tap, so the header must NOT promise "one
  // tap" (interaction-/ux-witness, 24 Oct) — it points the manager at the card instead.
  const hasBulkAction =
    canAct && dq.rows.some((r) => r.kind === 'stale-todo' || (r.suggestable ?? 0) > 0);

  const byId = useMemo(() => new Map(issues.map((i) => [i.id, i] as const)), [issues]);
  const phaseOf = (i: Issue): Pick<Phase, 'name' | 'targetEnd'> | null => {
    const p = i.phase ? phases.find((ph) => ph.id === i.phase) : undefined;
    return p ? { name: p.name, targetEnd: p.targetEnd } : null;
  };
  const sprintOf = (i: Issue): Pick<Sprint, 'name' | 'endsOn'> | null => {
    const s = i.sprint ? sprints.find((sp) => sp.id === i.sprint) : undefined;
    return s ? { name: s.name, endsOn: s.endsOn } : null;
  };

  // The three actions — each confirms first (naming the count), writes only the
  // confident suggestions, and is reversible: a filled value can be changed, a
  // backlogged card moved back. The suggesters are asked again here so the value
  // written is exactly the one the card's own assist would have offered.
  async function fillSizes(ids: string[]) {
    const toApply = ids
      .map((id) => byId.get(id))
      .filter((c): c is Issue => !!c)
      .map((c) => ({ id: c.id, sz: suggestSize(c, issues) }))
      .filter((x) => x.sz);
    if (!toApply.length) return;
    const n = toApply.length;
    const ok = await confirmDialog({
      title: 'Fill in sizes',
      body: `Set a suggested size on ${n} ${plural(n, 'card', 'cards')}? Each is the size most like it on this board, and you can change any card afterwards.`,
      confirmLabel: `Fill ${n}`,
      tone: 'caution',
    });
    if (!ok) return;
    for (const x of toApply) updateIssue(x.id, { size: x.sz!.size });
    // Confirm the write landed — the row drops away on the next snapshot, but a card
    // changing silently is "did that take?" (Vinnie, 28 Oct).
    showFlash(`Sized ${n} ${plural(n, 'card', 'cards')}`);
  }

  async function fillDues(ids: string[]) {
    const toApply = ids
      .map((id) => byId.get(id))
      .filter((c): c is Issue => !!c)
      .map((c) => {
        // History always supplied so piece C (comparable cycle-time) can answer an
        // owner-less card; piece D reads `owner` and skips when it is empty.
        const personal = { owner: ownerOf(c), history: issues, cal: workCalendar };
        return { id: c.id, due: suggestDue(c, phaseOf(c), issues, sprintOf(c), Date.now(), personal) };
      })
      .filter((x) => x.due);
    if (!toApply.length) return;
    const n = toApply.length;
    const ok = await confirmDialog({
      title: 'Fill in due dates',
      body: `Set a suggested due date on ${n} ${plural(n, 'card', 'cards')}? Each comes from how its owner usually works, the card's phase or sprint, or similar cards — and you can change any afterwards.`,
      confirmLabel: `Fill ${n}`,
      tone: 'caution',
    });
    if (!ok) return;
    for (const x of toApply) updateIssue(x.id, { dueDate: x.due!.due });
    showFlash(`Dated ${n} ${plural(n, 'card', 'cards')}`);
  }

  async function moveToBacklog(ids: string[]) {
    const n = ids.length;
    const ok = await confirmDialog({
      title: 'Move to Backlog',
      body: `Move ${n} stale To-Do ${plural(n, 'card', 'cards')} to the Backlog? They stop padding the work-in-progress count and the aging average; move any back whenever you like.`,
      confirmLabel: `Move ${n}`,
      tone: 'caution',
    });
    if (!ok) return;
    await moveIssues(ids, 'backlog');
    showFlash(`Moved ${n} to Backlog`);
  }

  // NOTHING TO DO — a single quiet green line, not a card full of ticks. The panel
  // earns its space by being loud only when there is a gap.
  if (dq.allClear) {
    return (
      <section className="panel dq-panel dq-clear">
        <span className="dq-clear-tick" aria-hidden="true"><Icon name="check" size={15} /></span>
        <span>
          <strong>Data Quality — all set.</strong> Every active card on {boardName} carries the
          facts the board’s numbers rely on.
        </span>
      </section>
    );
  }

  return (
    <section className="panel dq-panel">
      <div className="dq-head">
        <span className="dq-title">Data Quality</span>
        <InfoTip label="Data Quality">
          The facts the board’s own numbers lean on — a card’s size, its due date, whether it is
          still moving. A card missing one does not just go uncounted; it quietly bends the number
          that relies on it. This gathers those gaps for the whole board so you can close them in a
          tap, and tells you when a board is clean.
        </InfoTip>
        {dq.toReview > 0 && (
          <span className="dq-count">{dq.toReview} To Review</span>
        )}
      </div>
      <p className="dq-sub">
        {hasBulkAction ? (
          <>
            A few cards are missing the facts the board’s work-in-progress, aging and forecast
            numbers rely on. Fix them in one tap — or leave any you mean to.
          </>
        ) : canAct ? (
          <>
            A few cards are missing the facts the board’s work-in-progress, aging and forecast
            numbers rely on. Open a card to fill its gaps.
          </>
        ) : (
          <>
            A few cards are missing the facts the board’s work-in-progress, aging and forecast
            numbers rely on. Fill a card’s gaps as you open it; a board manager can clear these in
            bulk.
          </>
        )}
      </p>

      <div className="dq-rows">
        {dq.rows.map((row) => {
          const n = row.count;
          let title = '';
          let body = '';
          let action: React.ReactNode = null;
          if (row.kind === 'idle') {
            title = `${n} ${plural(n, 'card has', 'cards have')} gone quiet`;
            body =
              'No movement past their idle window. The people responsible have been nudged — worth a look if you own the board.';
          } else if (row.kind === 'stale-todo') {
            title = `${n} To-Do ${plural(n, 'card looks', 'cards look')} like backlog`;
            body = `No due date, and no movement in ${STALE_TODO_DAYS}+ days — they pad the work-in-progress count (${row.wipNow} → ${row.wipAfter}) and the average age.`;
            if (canAct) {
              action = (
                <button type="button" className="dq-act dq-act-primary" onClick={() => moveToBacklog(row.ids)}>
                  Move {n} to Backlog
                </button>
              );
            }
          } else if (row.kind === 'no-size') {
            title = `${n} ${plural(n, 'card has', 'cards have')} no size`;
            body =
              'Size feeds the idle window and the forecast.' +
              (canAct && row.suggestable > 0 ? ` FlightDeck can suggest ${row.suggestable} of them from similar cards.` : '');
            if (canAct && row.suggestable > 0) {
              action = (
                <button type="button" className="dq-act dq-act-primary" onClick={() => fillSizes(row.ids)}>
                  Fill {row.suggestable} {plural(row.suggestable, 'size', 'sizes')}
                </button>
              );
            }
          } else {
            title = `${n} ${plural(n, 'card has', 'cards have')} no due date`;
            body =
              'A due date puts the card on the forecast and the “coming up” list.' +
              (row.suggestable === 0
                ? ' They need a start date first.'
                : canAct
                  ? ` ${row.suggestable} can be suggested from a dated phase, sprint or similar cards.`
                  : '');
            if (canAct && row.suggestable > 0) {
              action = (
                <button type="button" className="dq-act dq-act-primary" onClick={() => fillDues(row.ids)}>
                  Fill {row.suggestable} {plural(row.suggestable, 'date', 'dates')}
                </button>
              );
            }
          }
          return (
            <div className="dq-row" key={row.kind}>
              <span className="dq-ic" aria-hidden="true"><Icon name={GAP_ICON[row.kind]} size={16} /></span>
              <div className="dq-row-main">
                <button
                  type="button"
                  className="dq-row-show"
                  onClick={() => showInBacklog(row.ids, showWhy(row.kind, n))}
                  title={`Show the ${n} ${plural(n, 'card', 'cards')} ${showWhy(row.kind, n)}`}
                >
                  {title}
                  <span className="dq-row-arrow" aria-hidden="true">→</span>
                </button>
                <div className="dq-row-body">{body}</div>
              </div>
              {action && <div className="dq-row-act">{action}</div>}
            </div>
          );
        })}
      </div>

      {dq.cleanDims.length > 0 && (
        <div className="dq-clean">
          {dq.cleanDims.map((d) => (
            <div className="dq-clean-line" key={d}>
              <span className="dq-clean-tick" aria-hidden="true"><Icon name="check" size={13} /></span>
              {CLEAN_LABEL[d]}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
